import { API_BASE_URL, apiClient } from '../../lib/apiClient';
import type {
  AssistantActionCard,
  AssistantChange,
  AssistantMessageCreate,
  AssistantMessageRead,
  AssistantThreadCreate,
  AssistantThreadDetail,
  AssistantThreadRead,
  AssistantThreadUpdate,
  ProposalStatusUpdate,
  SseTokenRead,
} from '../../types/api';

/**
 * Longest message the API accepts. Mirror of the backend's bound on
 * `AssistantMessageCreate.content`; the composer counts against it locally.
 */
export const ASSISTANT_MESSAGE_MAX_LENGTH = 4000;

/** Messages a user may send a day (`ASSISTANT_DAILY_TURNS`). The server enforces it. */
export const ASSISTANT_DAILY_LIMIT = 100;

/** Threads are a user's own, inside one brand. */
function threadsPath(businessId: string): string {
  return `/api/v1/businesses/${businessId}/assistant/threads`;
}

function threadPath(businessId: string, threadId: string): string {
  return `${threadsPath(businessId)}/${threadId}`;
}

/**
 * A message row as the client holds it: `changes` and `actions` are always
 * arrays. A server from before A2/A3 leaves them out, which means the turn
 * changed nothing and proposed nothing.
 */
export function normalizeAssistantMessage(message: AssistantMessageRead): AssistantMessageRead {
  if (
    Array.isArray(message.changes) &&
    Array.isArray(message.actions) &&
    Array.isArray(message.sources) &&
    typeof message.credits_used === 'number'
  ) {
    return message;
  }
  return {
    ...message,
    changes: Array.isArray(message.changes) ? message.changes : [],
    actions: Array.isArray(message.actions) ? message.actions : [],
    sources: Array.isArray(message.sources) ? message.sources : [],
    credits_used: typeof message.credits_used === 'number' ? message.credits_used : 0,
  };
}

/** The caller's conversations with this brand's assistant, newest activity first. */
export function fetchAssistantThreads(
  businessId: string,
  signal?: AbortSignal,
): Promise<AssistantThreadRead[]> {
  return apiClient.get<AssistantThreadRead[]>(threadsPath(businessId), { signal });
}

/** An empty conversation. The first message is posted separately. */
export function createAssistantThread(
  businessId: string,
  body: AssistantThreadCreate = {},
): Promise<AssistantThreadRead> {
  return apiClient.post<AssistantThreadRead>(threadsPath(businessId), { body });
}

/**
 * One conversation: its last 100 messages oldest first, whether a reply is
 * still being written, and how many messages are left today. 404
 * `RESOURCE_NOT_FOUND` for a thread that was deleted or is not the caller's.
 */
export async function fetchAssistantThread(
  businessId: string,
  threadId: string,
  signal?: AbortSignal,
): Promise<AssistantThreadDetail> {
  const detail = await apiClient.get<AssistantThreadDetail>(threadPath(businessId, threadId), {
    signal,
  });
  return {
    ...detail,
    messages: detail.messages.map(normalizeAssistantMessage),
    // A server from before A3 never watches anything.
    watching: detail.watching === true,
    // A server from before credits sends none: no meter rather than a wrong one.
    credits: detail.credits && typeof detail.credits.left === 'number' ? detail.credits : null,
  };
}

export function renameAssistantThread(
  businessId: string,
  threadId: string,
  title: string,
): Promise<AssistantThreadRead> {
  const body: AssistantThreadUpdate = { title };
  return apiClient.patch<AssistantThreadRead>(threadPath(businessId, threadId), { body });
}

/** Deletes the conversation. Anything already applied from it stays where it landed. */
export function deleteAssistantThread(businessId: string, threadId: string): Promise<void> {
  return apiClient.delete<void>(threadPath(businessId, threadId));
}

/**
 * Files the user's message and hands the reply to a worker. The 202 is the
 * *user* row; the reply arrives over the thread's event stream.
 *
 * 409 `ASSISTANT_REPLY_PENDING`: the last reply is still being written.
 * 429 `ASSISTANT_LIMIT_REACHED`: today's messages are spent.
 */
export async function sendAssistantMessage(
  businessId: string,
  threadId: string,
  body: AssistantMessageCreate,
): Promise<AssistantMessageRead> {
  const message = await apiClient.post<AssistantMessageRead>(
    `${threadPath(businessId, threadId)}/messages`,
    { body },
  );
  return normalizeAssistantMessage(message);
}

/**
 * Records what the user did with one proposal, after the record itself was
 * written through the calendar's endpoints. Answers the updated message row.
 */
export async function setAssistantProposalStatus(
  businessId: string,
  threadId: string,
  messageId: string,
  body: ProposalStatusUpdate,
): Promise<AssistantMessageRead> {
  const message = await apiClient.post<AssistantMessageRead>(
    `${threadPath(businessId, threadId)}/messages/${messageId}/proposal-status`,
    { body },
  );
  return normalizeAssistantMessage(message);
}

/**
 * Puts back what one of the assistant's own edits changed, through the same
 * operation the screen would use. Answers the change with status `undone`.
 * Never starts an assistant turn.
 *
 * 409 `ASSISTANT_UNDO_STALE`: the record was changed again since.
 * 409 `ASSISTANT_UNDO_DONE`: already undone.
 * 409 `ASSISTANT_UNDO_EXPIRED`: older than the 24 h window.
 * 422 `ASSISTANT_NOT_UNDOABLE`: the change carries no undo.
 * 404: not the caller's.
 */
export function undoAssistantAction(
  businessId: string,
  actionId: string,
): Promise<AssistantChange> {
  return apiClient.post<AssistantChange>(
    `/api/v1/businesses/${businessId}/assistant/actions/${actionId}/undo`,
  );
}

/**
 * A short-lived token scoped to one thread's event stream. `EventSource` cannot
 * send a header, so this, never the session token, rides in the stream's URL.
 */
export function mintAssistantSseToken(
  businessId: string,
  threadId: string,
  signal?: AbortSignal,
): Promise<SseTokenRead> {
  return apiClient.post<SseTokenRead>(`${threadPath(businessId, threadId)}/sse-token`, { signal });
}

/** The thread's stream: top level, like generation events, keyed by the minted token. */
export function assistantEventsUrl(threadId: string, token: string): string {
  const url = new URL(`${API_BASE_URL}/api/v1/assistant/threads/${threadId}/events`);
  url.searchParams.set('token', token);
  return url.toString();
}

function actionPath(businessId: string, actionId: string): string {
  return `/api/v1/businesses/${businessId}/assistant/actions/${actionId}`;
}

/**
 * Sets off a card. Answers 200 with the card at `done` or `failed` (an
 * operation that refused says why in `result.message`), then the server
 * enqueues a follow-up turn that reports on it.
 *
 * 409 `ASSISTANT_ACTION_NOT_PENDING`: already confirmed, cancelled or failed.
 * 409 `ASSISTANT_ACTION_EXPIRED`: past the card's 24 h window.
 * 404: not the caller's.
 */
export function confirmAssistantAction(
  businessId: string,
  actionId: string,
): Promise<AssistantActionCard> {
  return apiClient.post<AssistantActionCard>(`${actionPath(businessId, actionId)}/confirm`);
}

/** Puts a card away unused: `cancelled`, no follow-up. Same errors as confirm. */
export function cancelAssistantAction(
  businessId: string,
  actionId: string,
): Promise<AssistantActionCard> {
  return apiClient.post<AssistantActionCard>(`${actionPath(businessId, actionId)}/cancel`);
}
